// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web, reporting) — accès à l'API /api/dispositifs/reporting (backend/src/dispositifs/reporting/
// README.md § 2) : exercices, sept états, comparaison N / N-1, rapport annuel (provisoire, arrêt, réédition), état de
// campagne (agrégé, détail nominatif paginé, arrêt), documents arrêtés, exports Excel / PDF / HTML (fichier tracé).
// Lecture seule côté web : aucun chiffre n'est recalculé ici. Le serveur est seul juge des droits
// (ds_rapport_lire, ds_export_nominatif, ds_rapport_arreter) ; ses refus sont rendus tels quels.
// ═══════════════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const RP = '/api/dispositifs/reporting';

/** Erreur d'un téléchargement (corps Blob) : relit le JSON du serveur. */
async function erreurFichier(e) {
  const r = e?.response;
  if (r && typeof Blob !== 'undefined' && r.data instanceof Blob) {
    try {
      const d = JSON.parse(await r.data.text());
      return { statut: r.status, code: d.code || null, message: d.message || 'Export impossible.', erreurs: Array.isArray(d.erreurs) ? d.erreurs : [] };
    } catch { /* corps non JSON */ }
  }
  if (!r) return { statut: 0, code: 'RESEAU', message: 'Le serveur ne répond pas. Vérifiez la connexion puis réessayez.', erreurs: [] };
  return { statut: r.status, code: null, message: 'Export impossible.', erreurs: [] };
}

/** Nom du fichier lu dans Content-Disposition (attachment; filename="…"). */
function nomFichier(entete, defaut) {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(String(entete || ''));
  return m ? decodeURIComponent(m[1]) : defaut;
}

function client(token) {
  const entetes = token ? { Authorization: `Bearer ${token}` } : {};
  const get = async (url, params) => (await axios.get(url, { params, headers: entetes })).data.data;
  const post = async (url, corps, cle) => (await axios.post(url, corps || {}, { headers: cle ? { ...entetes, 'Idempotency-Key': cle } : entetes })).data.data;
  const sansVide = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined));
  /** Export : { blob, nom, empreinte } ; erreur normalisée dans `e.normalisee`. */
  const fichier = async (url, params, defaut) => {
    try {
      const r = await axios.get(url, { params: sansVide(params || {}), headers: entetes, responseType: 'blob' });
      return { blob: r.data, nom: nomFichier(r.headers['content-disposition'], defaut), empreinte: r.headers['x-empreinte-sha256'] || null };
    } catch (e) { throw Object.assign(new Error('export'), { normalisee: await erreurFichier(e) }); }
  };
  return {
    exercices: (org) => get(`${RP}/organisations/${org}/exercices`),
    etat: (org, code, exercice) => get(`${RP}/organisations/${org}/etats/${code}`, sansVide({ exercice })),
    exporterEtat: (org, code, exercice, format) => fichier(`${RP}/organisations/${org}/etats/${code}/export`, { exercice, format }, `etat-${code}.${format}`),
    rapport: (org, exercice) => get(`${RP}/organisations/${org}/rapport-annuel`, sansVide({ exercice })),
    exporterRapport: (org, exercice, format) => fichier(`${RP}/organisations/${org}/rapport-annuel/export`, { exercice, format }, `rapport-annuel.${format}`),
    comparaison: (org, exercice) => get(`${RP}/organisations/${org}/comparaison`, sansVide({ exercice })),
    arreterRapport: (org, annee, cle) => post(`${RP}/organisations/${org}/rapport-annuel/${annee}/arreter`, {}, cle),
    etatCampagne: (id) => get(`${RP}/campagnes/${id}/etat`),
    exporterEtatCampagne: (id, format) => fichier(`${RP}/campagnes/${id}/etat/export`, { format }, `etat-campagne.${format}`),
    detailCampagne: (id, { page = 1, taille = 25 } = {}) => get(`${RP}/campagnes/${id}/detail`, { page, taille }),
    exporterDetailCampagne: (id, format) => fichier(`${RP}/campagnes/${id}/detail/export`, { format }, `etat-campagne-nominatif.${format}`),
    arreterCampagne: (id, cle) => post(`${RP}/campagnes/${id}/arreter`, {}, cle),
    arretes: (org, { type, exercice, page = 1, taille = 25 } = {}) => get(`${RP}/organisations/${org}/arretes`, sansVide({ type, exercice, page, taille })),
    arrete: (id) => get(`${RP}/arretes/${id}`),
    exporterArrete: (id, format) => fichier(`${RP}/arretes/${id}/export`, { format }, `document-arrete.${format}`),
    reediter: (id, motif, cle) => post(`${RP}/arretes/${id}/reediter`, { motif }, cle),
  };
}

/** Client du reporting pour le composant courant. */
export function useRapports() {
  const { token } = useAuth();
  return useMemo(() => client(token), [token]);
}

export const ETATS = [
  ['gouvernance', 'Gouvernance'], ['prets', 'Prêts sociaux'], ['aides', 'Aides'], ['campagnes', 'Campagnes'],
  ['creances', 'Créances sur salariés'], ['tresorerie', 'Trésorerie (démonstration)'], ['retenues', 'Retenues (simulation)'],
];
export const LIBELLES_ETATS = Object.fromEntries(ETATS);
/** États dont les lignes portent un dispositif (filtre « dispositif » applicable). */
export const ETATS_PAR_DISPOSITIF = ['prets', 'aides', 'campagnes'];
export const FORMATS = [['xlsx', 'Excel'], ['pdf', 'PDF'], ['html', 'HTML']];
export const TYPES_DOCUMENT = { RAPPORT_ANNUEL: 'Rapport annuel', ETAT_CAMPAGNE: 'État de campagne' };
export const STATUTS_DOCUMENT = { ARRETE: ['succes', 'Arrêté (en vigueur)'], REMPLACE: ['neutre', 'Remplacé par une version suivante'] };
